import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Users, Crown, Heart, ChevronDown, Check } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Componente per gestire più assistiti (Multi-Assistito)
 * Premium: fino a 2 assistiti
 * Standard/Fondatore: 1 assistito (mostra upgrade)
 */
const MultiAssistitoSelector = ({ 
  assistiti, 
  selectedAssistito, 
  onSelectAssistito, 
  onAddAssistito,
  isPremium,
  onUpgrade,
  userId
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAssistitoName, setNewAssistitoName] = useState('');
  const [adding, setAdding] = useState(false);

  const maxAssistiti = isPremium ? 2 : 1;
  const canAddMore = assistiti.length < maxAssistiti;

  const handleAddClick = () => {
    if (!isPremium && assistiti.length >= 1) {
      // Non Premium con già 1 assistito -> mostra upgrade
      onUpgrade();
      return;
    }
    setShowAddForm(true);
  };

  const handleAddAssistito = async () => {
    if (!newAssistitoName.trim()) {
      toast.error('Inserisci il nome dell\'assistito');
      return;
    }

    setAdding(true);
    try {
      const response = await fetch(`${API_URL}/api/assistito/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          nome_assistito: newAssistitoName.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${newAssistitoName} aggiunto!`);
        setNewAssistitoName('');
        setShowAddForm(false);
        if (onAddAssistito) onAddAssistito(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nell\'aggiunta');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    } finally {
      setAdding(false);
    }
  };

  // Se c'è solo un assistito e non è Premium, mostra versione semplice
  if (assistiti.length <= 1 && !isPremium) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-2 bg-[#E8F5E9] rounded-xl">
          <Heart className="w-4 h-4 text-[#4A7C59]" />
          <span className="font-semibold text-[#2C3E50]">
            {selectedAssistito?.nome_assistito || 'Assistito'}
          </span>
        </div>
        
        {/* Bottone + per upgrade */}
        <button
          onClick={handleAddClick}
          className="w-8 h-8 rounded-full bg-[#E5E7EB] hover:bg-[#D1D5DB] flex items-center justify-center transition-colors group"
          title="Aggiungi assistito (Premium)"
        >
          <Plus className="w-4 h-4 text-[#64748B] group-hover:text-[#4A7C59]" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Selector Dropdown */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center gap-2 px-4 py-2 bg-[#E8F5E9] hover:bg-[#D5F5E3] rounded-xl transition-colors"
        >
          <Heart className="w-4 h-4 text-[#4A7C59]" />
          <span className="font-semibold text-[#2C3E50]">
            {selectedAssistito?.nome_assistito || 'Seleziona'}
          </span>
          <ChevronDown className={`w-4 h-4 text-[#64748B] transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>

        {/* Bottone + */}
        {canAddMore && (
          <button
            onClick={handleAddClick}
            className="w-8 h-8 rounded-full bg-[#4A7C59] hover:bg-[#3A6345] flex items-center justify-center transition-colors"
            title="Aggiungi assistito"
          >
            <Plus className="w-4 h-4 text-white" />
          </button>
        )}

        {!canAddMore && !isPremium && (
          <button
            onClick={onUpgrade}
            className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white text-xs font-semibold rounded-full hover:opacity-90"
            title="Passa a Premium per aggiungere assistiti"
          >
            <Crown className="w-3 h-3" />
            Premium
          </button>
        )}
      </div>

      {/* Dropdown Lista Assistiti */}
      {showDropdown && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-[#E5E7EB] z-50 overflow-hidden">
          <div className="p-2">
            <p className="text-xs text-[#64748B] px-2 py-1">Seleziona assistito</p>
            {assistiti.map((assistito) => (
              <button
                key={assistito.id}
                onClick={() => {
                  onSelectAssistito(assistito);
                  setShowDropdown(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  selectedAssistito?.id === assistito.id 
                    ? 'bg-[#E8F5E9] text-[#4A7C59]' 
                    : 'hover:bg-[#F9FAFB]'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  selectedAssistito?.id === assistito.id ? 'bg-[#4A7C59]' : 'bg-[#E5E7EB]'
                }`}>
                  <Heart className={`w-4 h-4 ${selectedAssistito?.id === assistito.id ? 'text-white' : 'text-[#64748B]'}`} />
                </div>
                <span className="font-medium flex-1 text-left">{assistito.nome_assistito}</span>
                {selectedAssistito?.id === assistito.id && (
                  <Check className="w-4 h-4 text-[#4A7C59]" />
                )}
              </button>
            ))}
          </div>
          
          {canAddMore && (
            <div className="border-t border-[#E5E7EB] p-2">
              <button
                onClick={() => {
                  setShowDropdown(false);
                  handleAddClick();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-[#4A7C59] hover:bg-[#F9FAFB] rounded-lg"
              >
                <Plus className="w-4 h-4" />
                <span className="font-medium">Aggiungi assistito</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal Aggiungi Assistito */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-xl font-bold text-[#2C3E50] mb-4">Nuovo Assistito</h3>
            <p className="text-sm text-[#64748B] mb-4">
              Aggiungi una seconda persona da monitorare
            </p>
            
            <input
              type="text"
              value={newAssistitoName}
              onChange={(e) => setNewAssistitoName(e.target.value)}
              placeholder="Es. Nonna Maria, Zio Giovanni..."
              className="w-full px-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#4A7C59] focus:outline-none mb-4"
              autoFocus
            />

            <div className="flex gap-3">
              <Button
                onClick={() => setShowAddForm(false)}
                variant="outline"
                className="flex-1 rounded-xl"
              >
                Annulla
              </Button>
              <Button
                onClick={handleAddAssistito}
                disabled={adding || !newAssistitoName.trim()}
                className="flex-1 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl"
              >
                {adding ? 'Aggiunta...' : 'Aggiungi'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Click fuori per chiudere dropdown */}
      {showDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default MultiAssistitoSelector;
